import React from 'react';
import { Check, X, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import pricingLite from '../images/generated-v2/pricing-lite.png';
import pricingPlus from '../images/generated-v2/pricing-plus.png';
import pricingPro from '../images/generated-v2/pricing-pro.png';

export default function Pricing({ onOpenDownload }) {
  return (
    <section id="precios" className="bg-white py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 mb-4">Precios</span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-ink-950 mb-4 text-balance">
            Empezá gratis. Sin sorpresas.
          </h2>
          <p className="text-lg text-ink-500 leading-relaxed">
            Todos los planes incluyen los 11 tipos de DTE, módulo de compras, cobros y pagos locales, modo contingencia y soporte técnico.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {/* Lite */}
          <div className="relative bg-white rounded-3xl border border-ink-200 overflow-hidden hover:shadow-lg transition-all">
            <div className="absolute top-5 right-5 z-10 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold uppercase tracking-wider">
              Gratis siempre
            </div>
            <div className="aspect-[16/9] bg-ink-50 overflow-hidden">
              <img src={pricingLite} alt="" aria-hidden="true" className="w-full h-full object-cover" />
            </div>
            <div className="p-7">
              <h3 className="font-bold text-xl text-ink-950 mb-1">Lite</h3>
              <p className="text-[13px] text-ink-500 mb-5">Sin costo. Sin caducidad. Sin tarjeta.</p>
              <div className="mb-6">
                <span className="text-5xl font-bold tracking-tighter text-ink-950">Gratis</span>
              </div>
              <button onClick={onOpenDownload} className="w-full bg-ink-100 hover:bg-ink-200 text-ink-950 font-semibold py-3 rounded-xl transition mb-6 text-[14px] cursor-pointer">
                Descargar
              </button>
              <ul className="space-y-3 text-[14px]">
                {[
                  'Los 11 tipos de DTE',
                  '1 empresa',
                  'iCloud sync (iOS) incluido',
                  'Módulo de Compras (Gmail + OCR)',
                  'Cuentas por cobrar y pagar locales',
                  'Modo contingencia',
                  'Exportar Excel/CSV',
                ].map(f => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check size={16} strokeWidth={3} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-ink-600">{f}</span>
                  </li>
                ))}
                <li className="flex items-start gap-2.5">
                  <X size={16} strokeWidth={2} className="text-ink-300 mt-0.5 flex-shrink-0" />
                  <span className="text-ink-400">Breve anuncio antes de sincronizar</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Plus — featured */}
          <div className="relative bg-gradient-to-br from-brand-700 to-brand-600 rounded-3xl overflow-hidden text-white shadow-2xl shadow-brand-600/30 md:-translate-y-3">
            <div className="absolute top-5 right-5 z-10 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-brand-700 text-[11px] font-bold uppercase tracking-wider">
              Popular
            </div>
            <div className="aspect-[16/9] overflow-hidden">
              <img src={pricingPlus} alt="" aria-hidden="true" className="w-full h-full object-cover" />
            </div>
            <div className="p-7">
              <h3 className="font-bold text-xl mb-1">Plus</h3>
              <p className="text-[13px] text-white/65 mb-5">Para profesionales independientes.</p>
              <div className="mb-6">
                <span className="text-5xl font-bold tracking-tighter">$5.99</span>
                <span className="text-white/65 text-[15px] ml-1">/ mes</span>
              </div>
              <button onClick={onOpenDownload} className="w-full bg-white text-brand-700 font-semibold py-3 rounded-xl hover:bg-ink-100 transition mb-3 text-[14px] cursor-pointer">
                Comenzar ahora
              </button>
              <Link to="/cotizacion?plan=Plus" className="w-full flex items-center justify-center gap-1.5 text-white/80 hover:text-white text-[13px] mb-6 transition">
                <FileText size={14} />
                Solicitar cotización
              </Link>
              <ul className="space-y-3 text-[14px]">
                {[
                  'Todo lo de Lite',
                  'Hasta 2 empresas',
                  ['Sin anuncios', true],
                  'Exportar PDF por lotes',
                ].map(item => {
                  const [text, emph] = Array.isArray(item) ? item : [item, false];
                  return (
                    <li key={text} className="flex items-start gap-2.5">
                      <Check size={16} strokeWidth={3} className="text-cyan-300 mt-0.5 flex-shrink-0" />
                      <span className={emph ? 'font-semibold' : ''}>{text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Pro */}
          <div className="bg-white rounded-3xl border border-ink-200 overflow-hidden hover:shadow-lg transition-all">
            <div className="aspect-[16/9] bg-ink-50 overflow-hidden">
              <img src={pricingPro} alt="" aria-hidden="true" className="w-full h-full object-cover" />
            </div>
            <div className="p-7">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-xl text-ink-950">Pro</h3>
                <span className="text-[10px] font-semibold uppercase tracking-wider bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">Empresarial</span>
              </div>
              <p className="text-[13px] text-ink-500 mb-5">Para empresas en crecimiento.</p>
              <div className="mb-6">
                <span className="text-5xl font-bold tracking-tighter text-ink-950">$9.99</span>
                <span className="text-ink-500 text-[15px] ml-1">/ mes</span>
              </div>
              <button onClick={onOpenDownload} className="w-full bg-ink-950 hover:bg-ink-900 text-white font-semibold py-3 rounded-xl transition mb-3 text-[14px] cursor-pointer">
                Comenzar ahora
              </button>
              <Link to="/cotizacion?plan=Pro" className="w-full flex items-center justify-center gap-1.5 text-ink-500 hover:text-ink-700 text-[13px] mb-6 transition">
                <FileText size={14} />
                Solicitar cotización
              </Link>
              <ul className="space-y-3 text-[14px]">
                {[
                  'Todo lo de Plus',
                  'Empresas ilimitadas',
                  'Envío PDF automático al cliente',
                  'Respaldo extra: Azure + Google Drive',
                  'Exportar JSON, PDF batch, CSV',
                ].map(f => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check size={16} strokeWidth={3} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-ink-600">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto mt-10 rounded-2xl border border-ink-100 bg-ink-50 px-6 py-5 text-center">
          <p className="text-[15px] text-ink-600 leading-relaxed">
            <strong className="font-semibold text-ink-950">¿Trabajás en el celular y en la compu? Pro+Sync.</strong>{' '}
            Sincronizá tus facturas, clientes, productos, compras y tickets del POS entre tus dispositivos
            con la misma cuenta — «Vincular empresa», «Sincronizar ahora» o modo automático con varios equipos.{' '}
            <strong className="font-semibold text-ink-950">Prueba gratis de 1 mes, sin tarjeta</strong>, una sola vez por cuenta.
          </p>
        </div>

        <p className="text-center text-[13px] text-ink-400 mt-10">
          Cancela cuando quieras. Sin permanencia. Sin contrato.
        </p>
      </div>
    </section>
  );
}
